export default function UseCaseSection() {
  const useCases = [
    {
      category: 'MANUFACTURING & CONSTRUCTION',
      title: '「匠の技」をビッグデータ化',
      description: '熟練工の視線・動作を解析し、技能伝承と自動化を実現する「フィジカルAI」を開発。',
      image: 'https://readdy.ai/api/search-image?query=Detailed%20skeleton%20detection%20overlay%20on%20a%20skilled%20factory%20worker%20performing%20precision%20assembly%20work%2C%20blue%20wireframe%20lines%20showing%20body%20joints%20and%20posture%2C%20professional%20industrial%20training%20analysis%20visualization%2C%20high%20tech%20motion%20capture%20style%2C%20clean%20technical%20interface%20design%2C%20automotive%20manufacturing%20setting&width=800&height=560&seq=usecase-manu-001&orientation=landscape',
      color: '#ff6b35',
      icon: 'ri-hammer-line',
      stats: [
        { label: '習熟度', value: '2倍', unit: '向上' },
        { label: '研修時間', value: '75%', unit: '削減' }
      ]
    },
    {
      category: 'LOGISTICS & INFRASTRUCTURE',
      title: '倉庫・施設の全動線を360度解析',
      description: 'ボトルネックを特定し、レイアウトと人員配置を最適化。',
      image: 'https://readdy.ai/api/search-image?query=Warehouse%20floor%20plan%20with%20colorful%20heatmap%20overlay%20showing%20movement%20patterns%20and%20traffic%20flow%2C%20red%20and%20orange%20zones%20indicating%20high%20activity%20areas%2C%20blue%20pathways%20showing%20worker%20routes%2C%20top%20down%20aerial%20perspective%2C%20logistics%20optimization%20visualization%2C%20modern%20distribution%20center%20environment&width=800&height=560&seq=usecase-logi-001&orientation=landscape',
      color: '#00d4ff',
      icon: 'ri-truck-line',
      stats: [
        { label: '作業効率', value: '40%', unit: '改善' },
        { label: 'ボトルネック', value: '95%', unit: '検出' }
      ]
    },
    {
      category: 'MEDICAL & HIGH-STAKES OPS',
      title: '医療・重要オペレーションの品質向上',
      description: '手術室や緊急対応の全プロセスを記録・解析。ミスを防ぎ、チーム連携を最適化。',
      image: 'https://readdy.ai/api/search-image?query=Modern%20operating%20room%20with%20surgical%20team%20performing%20procedure%2C%20overhead%20360%20degree%20view%20showing%20medical%20equipment%20and%20monitors%2C%20professional%20healthcare%20environment%20with%20advanced%20technology%2C%20clean%20sterile%20lighting%2C%20medical%20staff%20in%20surgical%20gowns%2C%20high%20tech%20hospital%20setting%20with%20data%20visualization%20overlays&width=800&height=560&seq=usecase-medical-001&orientation=landscape',
      color: '#4ade80',
      icon: 'ri-heart-pulse-line',
      stats: [
        { label: '手技精度', value: '3倍', unit: '向上' },
        { label: 'インシデント', value: '80%', unit: '削減' }
      ]
    },
  ];

  return (
    <section className="relative w-full py-32 bg-[#1a1d23]">
      <div className="container mx-auto px-8">
        {/* Title */}
        <div className="mb-20 text-center">
          <h2 className="text-6xl font-black text-white mb-3" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            Powering Physical AI
          </h2>
          <p className="text-2xl text-[#00d4ff] font-bold mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            現場の「フィジカルAI」を駆動するユースケース
          </p>
          <p className="text-lg text-[#b0b8c1]" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            産業現場での実装事例
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {useCases.map((useCase, index) => (
            <div
              key={index}
              className="group relative bg-[#0f1115] rounded-3xl border border-[#2a2e35] overflow-hidden hover:border-[#00d4ff] transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,212,255,0.3)] cursor-pointer"
            >
              {/* Image Area */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={useCase.image}
                  alt={useCase.title}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f1115] via-transparent to-transparent"></div>
                
                {/* Category Badge */}
                <div className="absolute top-6 left-6">
                  <span
                    className="px-4 py-2 rounded-full text-xs font-bold tracking-wider flex items-center gap-2"
                    style={{ backgroundColor: useCase.color, color: '#000' }}
                  >
                    <i className={`${useCase.icon} text-base`}></i>
                    {useCase.category}
                  </span>
                </div>
              </div>

              {/* Text Area */}
              <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  {useCase.title}
                </h3>
                <p className="text-base text-[#b0b8c1] leading-relaxed mb-6" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  {useCase.description}
                </p>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {useCase.stats.map((stat, idx) => (
                    <div key={idx} className="bg-[#1a1d23] rounded-lg p-4 border border-[#2a2e35]">
                      <div className="text-xs text-[#b0b8c1] mb-1">{stat.label}</div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-black" style={{ color: useCase.color }}>
                          {stat.value}
                        </span>
                        <span className="text-sm text-white font-bold">{stat.unit}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Arrow Icon */}
                <div className="flex items-center gap-2 text-[#00d4ff] group-hover:gap-4 transition-all duration-300">
                  <span className="text-sm font-bold tracking-wider">詳しく見る</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Scalability Message */}
        <div className="mt-20 text-center">
          <div className="inline-block bg-gradient-to-r from-[#00d4ff]/10 to-[#ff6b35]/10 border border-[#00d4ff]/30 rounded-2xl px-12 py-8">
            <div className="flex items-center justify-center gap-4 mb-4">
              <i className="ri-stack-line text-4xl text-[#00d4ff]"></i>
              <h3 className="text-3xl font-black text-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                スケーラビリティ
              </h3>
            </div>
            <p className="text-lg text-[#b0b8c1] max-w-3xl" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              一度構築した空間ビッグデータ基盤は、製造・物流・医療など、<br />
              あらゆる産業現場に横展開可能。データが蓄積されるほど、AIの精度が向上します。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
